/**
 * Admin · retención y supresión (008 · Fase 4.5).
 * Clarify: confirmación fuerte; API `/retention/*`.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { Link } from 'react-router'

import { useSesion } from '../../auth'
import { api } from '../../lib/api'
import type { ErasureBody, RetentionStatus } from '../../lib/tipos'

export function confirmacionCoincide(
    modo: 'contact' | 'lista',
    confirmacion: string,
    contactId: string,
    numConv: number,
): boolean {
    if (modo === 'contact') return confirmacion === contactId.trim() && contactId.trim().length > 0
    return confirmacion === String(numConv) && numConv > 0
}

export function AdminRetencion() {
    const { data: sesion } = useSesion()
    const qc = useQueryClient()
    const [modo, setModo] = useState<'contact' | 'lista'>('contact')
    const [contactId, setContactId] = useState('')
    const [listaRaw, setListaRaw] = useState('')
    const [confirmacion, setConfirmacion] = useState('')
    const [msg, setMsg] = useState<string | null>(null)

    const status = useQuery({
        queryKey: ['retention-status'],
        queryFn: () => api.get<RetentionStatus>('/retention/status'),
        enabled: sesion?.role === 'admin',
    })

    const conversaciones = useMemo(() => {
        return listaRaw
            .split('\n')
            .map((l) => l.trim())
            .filter(Boolean)
            .map((l) => {
                const [a, c] = l.split(/[,:\s]+/).map((x) => Number(x))
                return { accountId: a!, conversationId: c! }
            })
            .filter((x) => Number.isFinite(x.accountId) && Number.isFinite(x.conversationId))
    }, [listaRaw])

    const okConfirm = confirmacionCoincide(modo, confirmacion, contactId, conversaciones.length)

    const run = useMutation({
        mutationFn: () => api.post<{ runId: number }>('/retention/run', {}),
        onSuccess: () => {
            setMsg('Job disparado.')
            void qc.invalidateQueries({ queryKey: ['retention-status'] })
        },
        onError: () => setMsg('No se pudo disparar el job (¿ya en curso?).'),
    })

    const erase = useMutation({
        mutationFn: () => {
            const body: ErasureBody =
                modo === 'contact'
                    ? { contactId: Number(contactId), confirmacion }
                    : { conversaciones, confirmacion }
            return api.post<{ erasureId: number; borradas: { conversaciones: number; auditEvents: number } }>(
                '/retention/erase',
                body,
            )
        },
        onSuccess: (r) => {
            setMsg(
                `Supresión ok · erasure #${r.erasureId} · conv ${r.borradas.conversaciones} · audit ${r.borradas.auditEvents}`,
            )
            setConfirmacion('')
        },
        onError: () => setMsg('Supresión fallida (confirmación, retención en curso u otro error).'),
    })

    if (sesion?.role !== 'admin') {
        return <p className="p-6 text-sm text-neutral-500">No encontrado.</p>
    }

    const u = status.data?.ultimaEjecucion

    return (
        <div className="mx-auto max-w-2xl space-y-8 md:p-6">
            <header>
                <Link to="/" className="text-xs text-neutral-500 hover:text-neutral-300">
                    ← Toda la operación
                </Link>
                <h2 className="text-lg font-semibold text-neutral-100">Retención y supresión</h2>
                <p className="mt-1 text-xs text-neutral-500">
                    Plazo {status.data?.plazoDias ?? 90} días desde última actividad · job diario 03:00 UTC
                </p>
            </header>

            <section className="space-y-2 rounded-lg border border-neutral-800 p-4" aria-label="Estado del job">
                <h3 className="text-xs font-medium uppercase tracking-wide text-neutral-500">Última ejecución</h3>
                {status.isPending && <p className="text-xs text-neutral-600">Cargando…</p>}
                {status.isError && <p className="text-xs text-rose-400">No se pudo cargar el estado.</p>}
                {u ? (
                    <dl className="grid grid-cols-2 gap-2 text-xs text-neutral-300">
                        <dt className="text-neutral-500">Estado</dt>
                        <dd>{u.estado}</dd>
                        <dt className="text-neutral-500">Inicio</dt>
                        <dd>{u.startedAt}</dd>
                        <dt className="text-neutral-500">Procesadas</dt>
                        <dd>{u.conversacionesProcesadas}</dd>
                        {u.error && (
                            <>
                                <dt className="text-neutral-500">Error</dt>
                                <dd className="text-rose-400">{u.error}</dd>
                            </>
                        )}
                    </dl>
                ) : (
                    !status.isPending && <p className="text-xs text-neutral-600">Aún no ha corrido.</p>
                )}
                <button
                    type="button"
                    className="mt-2 rounded border border-neutral-700 px-3 py-1 text-xs text-neutral-200 hover:border-neutral-500"
                    onClick={() => run.mutate()}
                    disabled={run.isPending}
                >
                    Ejecutar ahora
                </button>
            </section>

            <section className="space-y-3 rounded-lg border border-neutral-800 p-4" aria-label="Supresión a petición">
                <h3 className="text-xs font-medium uppercase tracking-wide text-neutral-500">Supresión</h3>
                <div className="flex gap-2 text-xs">
                    <button
                        type="button"
                        className={modo === 'contact' ? 'text-neutral-100' : 'text-neutral-500'}
                        onClick={() => setModo('contact')}
                    >
                        Por contactId
                    </button>
                    <button
                        type="button"
                        className={modo === 'lista' ? 'text-neutral-100' : 'text-neutral-500'}
                        onClick={() => setModo('lista')}
                    >
                        Por lista
                    </button>
                </div>
                {modo === 'contact' ? (
                    <label className="block text-xs text-neutral-400">
                        contactId
                        <input
                            className="mt-1 w-full rounded border border-neutral-700 bg-neutral-950 px-2 py-1 text-neutral-100"
                            value={contactId}
                            onChange={(e) => setContactId(e.target.value)}
                            inputMode="numeric"
                        />
                    </label>
                ) : (
                    <label className="block text-xs text-neutral-400">
                        Conversaciones (una por línea: accountId,conversationId)
                        <textarea
                            className="mt-1 w-full rounded border border-neutral-700 bg-neutral-950 px-2 py-1 font-mono text-neutral-100"
                            rows={4}
                            value={listaRaw}
                            onChange={(e) => setListaRaw(e.target.value)}
                        />
                        <span className="text-neutral-600">{conversaciones.length} conversación(es)</span>
                    </label>
                )}
                <label className="block text-xs text-neutral-400">
                    Confirmación (reescribe el contactId o el número de conversaciones)
                    <input
                        className="mt-1 w-full rounded border border-neutral-700 bg-neutral-950 px-2 py-1 text-neutral-100"
                        value={confirmacion}
                        onChange={(e) => setConfirmacion(e.target.value)}
                        aria-label="Confirmación de supresión"
                    />
                </label>
                <button
                    type="button"
                    disabled={!okConfirm || erase.isPending}
                    className="rounded bg-amber-700/80 px-3 py-1.5 text-xs text-neutral-100 disabled:opacity-40"
                    onClick={() => erase.mutate()}
                >
                    Suprimir (irreversible)
                </button>
            </section>

            {msg && <p className="text-xs text-neutral-400">{msg}</p>}
        </div>
    )
}
